import { SelectMenuOption } from '@scale/scaleui';
import { ITeamLeadPodTypeJson } from '@scale/shared/types/expertManagement';
import { SelectOption } from 'react-scale/corp/components/UI/Select/Select';
import { create } from 'zustand';

type DateRangeOption = {
  id: string;
  name: string;
  lookbackPeriod: number;
  lookbackType: 'day' | 'toDate' | 'last' | 'hours' | 'rollingDays';
};

export const DateRangeOptions: DateRangeOption[] = [
  { id: 'days_1', name: 'Last 24 hours', lookbackPeriod: 24, lookbackType: 'hours' },
  { id: 'days_2', name: 'Last 2 full days', lookbackPeriod: 2, lookbackType: 'day' },
  { id: 'days_7', name: 'Last 7 full days', lookbackPeriod: 7, lookbackType: 'day' },
  { id: 'days_14', name: 'Last 14 full days', lookbackPeriod: 14, lookbackType: 'day' },
  { id: 'days_30', name: 'Last 30 full days', lookbackPeriod: 30, lookbackType: 'day' },
  { id: 'last_day', name: 'Yesterday', lookbackPeriod: 1, lookbackType: 'last' },
  { id: 'last_week', name: 'Last Week', lookbackPeriod: 2, lookbackType: 'last' },
  { id: 'custom_dtd', name: 'Today', lookbackPeriod: 1, lookbackType: 'toDate' },
  { id: 'custom_wtd', name: 'Week to date', lookbackPeriod: 2, lookbackType: 'toDate' },
  { id: 'custom_mtd', name: 'Month to date', lookbackPeriod: 3, lookbackType: 'toDate' },
  { id: 'custom_l7d', name: 'Last 7 rolling days', lookbackPeriod: 7, lookbackType: 'rollingDays' },
];

const DEFAULT_DATE_RANGE_OPTION = DateRangeOptions.find(option => option.id === 'days_7')!;

// TODO: refactor to get rid of context and use zustand state instead to prevent constant rerendering
export type StatContextState = {
  dateRange: string;
  setDateRange: (range: string) => void;
  dateRangeOption: DateRangeOption;
  project: SelectOption | null;
  setProject: (project: SelectOption | null) => void;
  projectId: string | undefined;
  podTypeFilter: string;
  setPodTypeFilter: (podTypeFilter: string) => void;
  podTypes: ITeamLeadPodTypeJson[];
  setPodTypes: (podTypes: ITeamLeadPodTypeJson[]) => void;
  podTypeOptions: SelectMenuOption<string>[];
};

function getDateRangeOption(dateRange: string) {
  const range = DateRangeOptions.find(option => option.id === dateRange);
  return range ?? DEFAULT_DATE_RANGE_OPTION;
}

const dateRangeKey = 'expertManagement.statContextDateRange';
const projectKey = 'expertManagement.statContextProject';
const podTypeFilterKey = 'expertManagement.statContextPodTypeFilter';

export const setDateRangeIfNotSet = (dateRange: string) => {
  if (!localStorage.getItem(dateRangeKey)) {
    localStorage.setItem(dateRangeKey, dateRange);
  }
};

export const useStatContextStore = create<StatContextState>(set => ({
  dateRange: localStorage.getItem(dateRangeKey) ?? 'days_7',
  dateRangeOption:
    DateRangeOptions.find(option => option.id === localStorage.getItem(dateRangeKey) ?? '') ??
    DEFAULT_DATE_RANGE_OPTION,
  setDateRange: dateRange => {
    set({ dateRange, dateRangeOption: getDateRangeOption(dateRange) });
    localStorage.setItem(dateRangeKey, dateRange);
  },
  project: JSON.parse(localStorage.getItem(projectKey) ?? 'null'),
  setProject: project => {
    set({ project, projectId: project?.value || undefined });
    localStorage.setItem(projectKey, JSON.stringify(project));
  },
  projectId: JSON.parse(localStorage.getItem(projectKey) ?? 'null')?.value,
  podTypeFilter: localStorage.getItem(podTypeFilterKey) ?? '65cb0e1a881781a04e5d0816',
  setPodTypeFilter: podTypeFilter => {
    set({ podTypeFilter });
    localStorage.setItem(podTypeFilterKey, podTypeFilter);
  },
  podTypes: [],
  setPodTypes: podTypes =>
    set({
      podTypes,
      podTypeOptions: podTypes.map((podType: ITeamLeadPodTypeJson) => ({
        name: podType.name,
        id: podType._id,
      })),
    }),
  podTypeOptions: [],
}));

export const dateRangeSelector = (state: StatContextState): StatContextState['dateRange'] =>
  state.dateRange;
export const setDateRangeSelector = (state: StatContextState): StatContextState['setDateRange'] =>
  state.setDateRange;
export const projectSelector = (state: StatContextState): StatContextState['project'] =>
  state.project;
export const setProjectSelector = (state: StatContextState): StatContextState['setProject'] =>
  state.setProject;
export const projectIdSelector = (state: StatContextState): StatContextState['projectId'] =>
  state.projectId;
export const podTypeFilterSelector = (state: StatContextState): StatContextState['podTypeFilter'] =>
  state.podTypeFilter;
export const setPodTypeFilterSelector = (
  state: StatContextState,
): StatContextState['setPodTypeFilter'] => state.setPodTypeFilter;
export const podTypesSelector = (state: StatContextState): StatContextState['podTypes'] =>
  state.podTypes;
export const setPodTypesSelector = (state: StatContextState): StatContextState['setPodTypes'] =>
  state.setPodTypes;
export const podTypeOptionsSelector = (
  state: StatContextState,
): StatContextState['podTypeOptions'] => state.podTypeOptions;
export const apiStatContextSelector = (state: StatContextState): ApiStatContext => ({
  dateRangeOption: state.dateRangeOption,
  podTypeFilter: state.podTypeFilter,
  projectId: state.projectId,
});

export type ApiStatContext = {
  dateRangeOption: DateRangeOption;
  podTypeFilter: string;
  projectId?: string;
};
